import { User, onAuthStateChanged } from "firebase/auth";
import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { WebSettings } from "./dto/WebSettings";
import { auth } from "./firebase/firebaseConfig";
import { signOut } from "firebase/auth";
import { showErrorToast, showSuccessToast } from "./components/Toastify/toastHelper";
import { UserService } from "./services/UserService";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

interface AppContextType {
  search: string;
  setSearch: (value: string) => void;
  user: any;
  setUser: (value: any) => void;
  role: string;
  setRole: (value: string) => void;
  webSettings: WebSettings | null;
  setWebSettings: (value: WebSettings) => void;
  publishedBooksCount: number;
  setPublishedBooksCount: (value: number) => void;
  modalMode: string;
  setModalMode: (value: string) => void;
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  showLogoutModal: boolean;
  setShowLogoutModal: (value: boolean) => void;
  language: string;
  setLanguage: (value: "EN" | "FR" | "AR") => void;
  cartCount: number;
  setCartCount: (value: number) => void;
  logout: () => Promise<void>;
  logoutNotActive: () => Promise<void>;
  refreshUser: () => Promise<void>;
  t: TFunction;
  authLoading: boolean;
  setAuthLoading: (value: boolean) => void;
  userDataLoading: boolean;
  setUserDataLoading: (value: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [search, setSearch] = useState<string>("");
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState('');
  const [webSettings, setWebSettings] = useState<WebSettings | null>(null);
  const [publishedBooksCount, setPublishedBooksCount] = useState<number>(0);
  const [modalMode, setModalMode] = useState<string>('login');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);
  const [language, setLanguage] = useState<"EN" | "FR" | "AR">(() => {
    const savedLanguage = localStorage.getItem('appLanguage');
    return (savedLanguage as "EN" | "FR" | "AR") || 'EN';
  });
  const [cartCount, setCartCount] = useState<number>(0);
  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [userDataLoading, setUserDataLoading] = useState<boolean>(true);
  const { t } = useTranslation();

  const refreshUser = async () => {
    try {
      const response = await UserService.getUser();
      setUser(response);
      setRole(response.role);
    } catch (err: any) {
   
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (!user) {
        setRole('');
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setRole('');
      setCartCount(0);
      showSuccessToast("toast.logoutSuccess", t);
    } catch (error) {
   
      showErrorToast("toast.logoutError", t);
      throw error;
    }finally{
      setShowModal(false);
    }
  };

  const handleLogoutNotActive = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setRole('');
      setCartCount(0);
      showSuccessToast("toast.logoutNotActive", t);
    } catch (error) {
   
      showErrorToast("toast.logoutError", t);
      throw error;
    }finally{
      setShowModal(false);
    }
  };

  useEffect(() => {
    localStorage.setItem('appLanguage', language);
  }, [language]);

  return (
    <AppContext.Provider value={{
      showModal, setShowModal,
      modalMode, setModalMode,
      showLogoutModal, setShowLogoutModal,
      search, setSearch,
      user, setUser,
      role, setRole,
      webSettings, setWebSettings,
      publishedBooksCount, setPublishedBooksCount,
      language, setLanguage,
      cartCount, setCartCount,
      logout: handleLogout,
      logoutNotActive: handleLogoutNotActive,
      refreshUser,
      t,
      authLoading,
      setAuthLoading,
      userDataLoading,
      setUserDataLoading
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};